import React, { useEffect, useState } from "react";
import { StyledTable, TableHeader, TitleContainer } from "../../../style";
import { IconButton, TableCell, Typography } from "@mui/material";
import ColumnContainer from "../../../../common/ColumnContainer";
import RowContainer from "../../../../common/RowContainer";
import { DashboardButton } from "../../../../common/DashboardButton";
import {
  Add,
  KeyboardArrowDownRounded,
  MonetizationOnRounded,
  Remove,
} from "@mui/icons-material";
import {
  useGetSolutionsListQuery,
  useUpdateSolutionsListMutation,
} from "../../../../../redux/features/solutions/solutionsApi";
import { getSolutionsList } from "../../../../../redux/features/solutions/solutionsSlice";
import { useDispatch, useSelector } from "react-redux";
import { selectStep } from "../../../../../redux/features/credits/creditsSlice";
import { SnackbarComponent } from "../../../../common/SnackbarComponent";
import { useAuth } from "../../../../../utils/hooks/useAuth";
import { FormattedMessage } from "react-intl";
import { SkeletonLoading } from "../../../../common/SkeletonLoading";
import { ErrorLoading } from "../../../../common/ErrorLoading";

const PricingServices = () => {
  // INIT
  const dispatch = useDispatch();
  const { auth } = useAuth();
  const adminId = auth?.userData?.role === "admin" && auth?.userData?.id;
  // STATE

  const [solutions, setSolutions] = useState(null);
  const [openCard, setOpenCard] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  // REDUX
  const {
    data: allSolutions,
    isLoading,
    isError,
  } = useGetSolutionsListQuery({
    adminId: adminId,
  });
  const [updatePricing] = useUpdateSolutionsListMutation();
  const step = useSelector(selectStep);

  //EFFECTS
  useEffect(() => {
    if (allSolutions) {
      // Create a deep copy of allSolutions
      const mutableSolutions = JSON.parse(JSON.stringify(allSolutions));
      setSolutions(mutableSolutions);
      dispatch(getSolutionsList(mutableSolutions));
    }
  }, [allSolutions, dispatch]);

  // UTILS`
  const tableHeaders = [
    <FormattedMessage id="settings.listing.services.service" />,
    <RowContainer align="center" start minGap>
      <FormattedMessage id="settings.listing.services.price" />
      <MonetizationOnRounded
        fontSize="small"
        sx={{ "& path": { color: "var(--main-color)" } }}
      />
    </RowContainer>,
  ];

  // FUNCTIONS

  const handleOpenCard = () => {
    setOpenCard(!openCard);
  };
  const handleInputChange = (index, value) => {
    value = parseFloat(value.toFixed(2));
    if (!step) {
      setOpenSnackbar(true);
    }
    const newSolutions = [...solutions];
    newSolutions[index] = {
      ...newSolutions[index],
      price: value < 0 ? 0 : value,
    };
    setSolutions(newSolutions);
  };

  const renderPricingRows = () => {
    return allSolutions.map((sol, index) => (
      <tr key={index}>
        <TableCell>{sol.name}</TableCell>
        <TableCell>
          <RowContainer align="center" className="counter-container">
            <IconButton
              onClick={() =>
                handleInputChange(index, solutions[index].price - step)
              }
            >
              <Remove />
            </IconButton>
            <Typography>
              {solutions[index].price !== undefined
                ? solutions[index].price
                : sol.price}
            </Typography>
            <IconButton
              onClick={(e) =>
                handleInputChange(index, solutions[index].price + step)
              }
            >
              <Add />
            </IconButton>
          </RowContainer>
        </TableCell>
      </tr>
    ));
  };

  const saveChanges = () => {
    dispatch(getSolutionsList(solutions));
    updatePricing({ adminId: 2, solutions: solutions });
  };

  return (
    <ColumnContainer start className="pricing-services-page">
      <TitleContainer>
        <RowContainer start align="center">
          <Typography variant="h5" component="h5">
            <FormattedMessage id="settings.listing.services" />
          </Typography>
          <IconButton
            onClick={handleOpenCard}
            sx={{
              backgroundColor: "var(--bkg-quaternary)",
            }}
          >
            <KeyboardArrowDownRounded
              sx={{
                "& path": { color: "var(--main-color)" },
                cursor: "pointer",
                transform: openCard ? "rotate(180deg)" : "rotate(0deg)",
              }}
            />
          </IconButton>
        </RowContainer>
      </TitleContainer>
      {openCard ? (
        isLoading ? (
          <SkeletonLoading type="settings" />
        ) : isError ? (
          <ErrorLoading />
        ) : (
          <ColumnContainer start style={{ gap: "3rem" }}>
            <ColumnContainer>
              <StyledTable>
                <thead>
                  {tableHeaders.map((header, index) => (
                    <TableHeader key={index}>{header}</TableHeader>
                  ))}
                </thead>
                <tbody>{solutions?.length > 0 && renderPricingRows()}</tbody>
              </StyledTable>
              <RowContainer end padding={"20px"}>
                <DashboardButton type="filled" onClick={saveChanges}>
                  <FormattedMessage id="button.save" />
                </DashboardButton>
              </RowContainer>
            </ColumnContainer>
          </ColumnContainer>
        )
      ) : null}
      <SnackbarComponent
        message={<FormattedMessage id="snackbar.validStep" />}
        duration={7000}
        open={openSnackbar}
        handleClose={() => setOpenSnackbar(false)}
      ></SnackbarComponent>
    </ColumnContainer>
  );
};

export default PricingServices;
